import type { FC, SVGProps } from "react"
import { useState } from "react"
import { IconSquaresConnectedOutline18 as SquaresConnected } from "nucleo-ui-outline-18"

import { faviconUrlForPageUrl } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"

interface ConnectorLogoProps {
	url: string
	/** The connector's own logo; the URL's favicon when unset or broken. */
	logoUrl?: string | null
	className?: string
	sizeClassName?: string
	iconClassName?: string
	FallbackIcon?: FC<SVGProps<SVGSVGElement>>
}

export function ConnectorLogo({
	url,
	logoUrl,
	className,
	sizeClassName = "h-4 w-4",
	iconClassName = "h-3.5 w-3.5",
	FallbackIcon = SquaresConnected,
}: ConnectorLogoProps) {
	const [logoFailed, setLogoFailed] = useState(false)
	const useLogo = !!logoUrl && !logoFailed
	const src = useLogo ? logoUrl : faviconUrlForPageUrl(url, 64)
	const [readySrc, setReadySrc] = useState<string | null>(null)
	const ready = !!src && readySrc === src

	return (
		<span
			className={cn(
				"relative flex flex-none items-center justify-center overflow-hidden rounded",
				sizeClassName,
				className,
			)}
		>
			{!ready ? (
				<FallbackIcon className={cn("text-tertiary", iconClassName)} />
			) : null}
			{src ? (
				<img
					key={src}
					src={src}
					alt=""
					className={
						ready
							? "h-full w-full object-contain"
							: "pointer-events-none absolute h-0 w-0 opacity-0"
					}
					onError={() => {
						if (useLogo) setLogoFailed(true)
					}}
					onLoad={(e) => {
						// Google's missing-favicon placeholder is a tiny generic globe.
						const { naturalWidth, naturalHeight } = e.currentTarget
						if (!useLogo && (naturalWidth <= 16 || naturalHeight <= 16)) return
						setReadySrc(src)
					}}
				/>
			) : null}
		</span>
	)
}
